import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authAction } from "@redux/modules";
import showErrMessage from "@components/ShowErrMessage";
import LegalText from "@components/LegalText";
import { LEGAL_TEXT_TYPES } from "../../../../constants/legalText.const";
import "./styles.scss";
import "@assets/styles/public.scss";
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Modal } from 'react-bootstrap';
import InputMask from 'react-input-mask';

// Toastify
import showMessage from "@components/ShowMessage";

// Cookie yardımcı fonksiyonu
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const act = useSelector((store) => store.auth);
  const loginSettings = useSelector((store) => store.auth.loginSettings);

  const [loginType, setLoginType] = useState("username");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordType, setPasswordType] = useState("password");
  const [disabledButton, setDisabledButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMobileNo, setConfirmMobileNo] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [timer, setTimer] = useState(120); // 2 minutes in seconds
  const [timerExpired, setTimerExpired] = useState(false);
  const [timerKey, setTimerKey] = useState(0); // resend ile sayacı yeniden başlatmak için
  const [showAydinlatmaModal, setShowAydinlatmaModal] = useState(false);
  const [showKisiselVerilerModal, setShowKisiselVerilerModal] = useState(false);
  const [showCerezPolitikasiModal, setShowCerezPolitikasiModal] = useState(false);

  useEffect(() => {
    if (showConfirmModal) {
      setTimer(120); // Reset timer to 2 minutes
      setTimerExpired(false);
      const interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval);
            // Süre dolunca modalı kapatmıyoruz; kullanıcı "Tekrar Kod Gönder" ile
            // yeni kod isteyebilsin diye sadece sayacı dolmuş işaretliyoruz.
            setTimerExpired(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [showConfirmModal, timerKey]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  useEffect(() => {
    if (!showConfirmModal) {
      setConfirmMobileNo("");
      setConfirmCode("");
    }
  }, [showConfirmModal]);

  // Login başarılı olduğunda yönlendirme için useEffect.
  // Doğrudan kullanıcının hedef sayfasına navigate ederiz.
  // PrivateRoute global gate olarak çalışır:
  //   - Aktif disclaimer + geçerli onay yoksa, hedef sayfa yerine
  //     /sorumluluk-reddi-beyani'ye yönlendirir ve hedef sayfayı sessionStorage'a yazar.
  //   - Onay tamamlandığında Disclaimer sayfası bu hedefe geri yönlendirir.
  useEffect(() => {
    if (act.isAuthenticated && act.authUser) {
      const redirectTimer = setTimeout(() => {
        // Başarılı login sonrası her zaman /home açılır.
        navigate("/home", { replace: true });
      }, 100);

      return () => clearTimeout(redirectTimer);
    }
  }, [act.isAuthenticated, act.authUser, navigate]);

  // Telefon numarasını maskeli gösterir: 0532 *** ** 67
  const maskPhone = (phone) => {
    if (!phone) return "";
    const digits = String(phone).replace(/\D/g, "");
    if (digits.length < 6) return digits;
    return `${digits.slice(0, 4)} *** ** ${digits.slice(-2)}`;
  };

  const handleConfirmCode = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;

    if (form.checkValidity() !== false) {
      dispatch(authAction.registerVendorConfirm(confirmCode, confirmMobileNo))
        .then(async (e) => {
          if (e.isHttpSuccess) {
            showMessage("Telefon numaranız doğrulandı.");
            setShowConfirmModal(false);

            // Doğrulama başarılı: aynı bilgilerle otomatik giriş denenir.
            try {
              const response = await dispatch(
                authAction.login({ username, password })
              );
              if (response.redirectTo) {
                navigate(response.redirectTo, { replace: true });
              }
              // Normal login başarılı ise yönlendirme useEffect ile yapılacak
            } catch (error) {
              showErrMessage(error.message);
            }
          }
        })
        .catch((err) => {
          if (err.isHttpError) {
            showErrMessage(err.message);
          }
        });
    }
  };

  // Süre dolduğunda / kullanıcı yeniden istediğinde yeni doğrulama kodu gönderir.
  const handleResendCode = () => {
    dispatch(authAction.registerVendorResendCode(confirmMobileNo))
      .then((e) => {
        if (e.isHttpSuccess) {
          showMessage("Yeni doğrulama kodu telefon numaranıza gönderildi.");
          setConfirmCode("");
          setTimerKey((prev) => prev + 1); // sayacı yeniden başlat
        } else {
          showErrMessage("Kod gönderilemedi, lütfen tekrar deneyiniz.");
        }
      })
      .catch(() => {
        showErrMessage("Kod gönderilemedi, lütfen tekrar deneyiniz.");
      });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setDisabledButton(true);
    setLoading(true);

    // authData get from cookies
    let authData = getCookie(username);

    if (authData) {
      authData = JSON.parse(authData);
    }

    try {
      let response;
      if(authData?.uniqueField === username) {
        response = await dispatch(authAction.login({ username, password, proofKey: authData?.proofKey }));
      } else {
        response = await dispatch(authAction.login({ username, password }));
      }
      
      // Eğer özel yönlendirme gerekiyorsa (şifre değiştirme, 2FA vb.)
      if (response.redirectTo) {
        navigate(response.redirectTo, { replace: true });
      }
      // Normal login başarılı ise yönlendirme useEffect ile yapılacak
    } catch (error) {
      const data = error?.error?.response?.data;

      // Kod 1034: Telefon numarası doğrulanmamış. Backend bu noktada telefona
      // doğrulama kodu (SMS) gönderir; biz de kodu girebileceği modalı açıyoruz.
      if (data?.code === 1034) {
        // mobileNo backend'in döndüğü fields[0] içinde gelir; yoksa telefon ile
        // giriş yapılıyorsa girilen numarayı kullan.
        const mobileNo =
          data?.fields?.[0] || (loginType === "phone" ? username : "");
        setConfirmMobileNo(mobileNo);
        setConfirmCode("");
        setShowConfirmModal(true);
      }

      showErrMessage(error.message);
    } finally {
      setDisabledButton(false);
      setLoading(false);
    }
  };

  const handlePasswordTypeChange = () => {
    setPasswordType(passwordType === "password" ? "text" : "password");
  };

  return (
    <section>
      <div className="container-fluid">
        <div className="row align-items-stretch">
          <div className="col-lg-6 bg-gray-lightest">
            <div className="row min-vh-100 align-items-center justify-content-center">
              <div className="col-xxl-7 col-lg-10 col-md-8 col-10 pt-md-2 pt-1 pb-md-1 pb-1">
                <div className="text-center mb-2">
                  {loginSettings?.logo && (
                    <Link to="/" className="text-decoration-none">
                      <img
                        src={loginSettings?.logo}
                        alt="logo"
                        className="auth-logo"
                      />
                    </Link>
                  )}
                </div>
                <form onSubmit={handleLogin}>
                  <div className="btn-group w-100 mb-3" role="group" aria-label="Giriş tipi">
                    <button
                      type="button"
                      className={`btn w-50 ${loginType === "username" ? "btn-blue" : "btn-outline-secondary"} rounded-start-3`}
                      onClick={() => {
                        setLoginType("username");
                        setUsername("");
                      }}
                    >
                      <i className="fas fa-user me-2"></i>Kullanıcı Adı
                    </button>
                    <button
                      type="button"
                      className={`btn w-50 ${loginType === "phone" ? "btn-blue" : "btn-outline-secondary"} rounded-end-3`}
                      onClick={() => {
                        setLoginType("phone");
                        setUsername("");
                      }}
                    >
                      <i className="fas fa-phone-alt me-2"></i>Telefon
                    </button>
                  </div>
                  {loginType === "phone" ? (
                    <div className="form-floating shadow-lg mb-3 rounded-3">
                      <InputMask
                        mask="05(99) 999 99 99"
                        maskChar="_"
                        value={username}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "");
                          setUsername(digits);
                        }}
                      >
                        {(inputProps) => (
                          <input
                            {...inputProps}
                            type="text"
                            className="form-control border-0 rounded-3 px-4"
                            placeholder="05(__) ___ __ __"
                            inputMode="numeric"
                          />
                        )}
                      </InputMask>
                      <label htmlFor="loginUsername" className="px-4">
                        <i className="fas fa-phone-alt me-3"></i>Telefon Numarası
                      </label>
                    </div>
                  ) : (
                    <div className="form-floating shadow-lg mb-3 rounded-3">
                      <input
                        type="text"
                        className="form-control border-0 rounded-3 px-4"
                        placeholder="Kullanıcı Adı"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                      />
                      <label htmlFor="loginUsername" className="px-4">
                        <i className="fas fa-user me-3"></i>Kullanıcı Adı
                      </label>
                    </div>
                  )}
                  <div className="form-floating shadow-lg mb-2 rounded-3">
                    <input
                      type={passwordType}
                      className="form-control border-0 rounded-3 px-4"
                      id="loginPassword"
                      placeholder="Şifre"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <label htmlFor="loginPassword" className="px-4">
                      <i className="fas fa-lock me-3"></i>Şifre
                    </label>
                    {password?.length > 0 && (
                      <>
                        <FaEye
                          className={
                            passwordType === "password"
                              ? "d-none password-eye"
                              : "password-eye"
                          }
                          onClick={handlePasswordTypeChange}
                        />
                        <FaEyeSlash
                          className={
                            passwordType === "text"
                              ? "d-none password-eye-slash"
                              : "password-eye-slash"
                          }
                          onClick={handlePasswordTypeChange}
                        />
                      </>
                    )}
                  </div>
                  <div className="text-end mb-2">
                    <Link to={{ pathname: "/passwordReset" }}>
                      Şifremi Unuttum
                    </Link>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-blue py-sm-3 py-2 px-sm-4 px-3 rounded-3 w-100 mb-2"
                    disabled={disabledButton || loading}
                  >
                    {loading && (
                      <span className="spinner-border spinner-border-sm me-3"></span>
                    )}
                    Giriş Yap <i className="fas fa-arrow-right ms-3"></i>
                  </button>
                  <div className="text-center text-gray small">
                    Hesabınız yok mu?{" "}
                    <Link to={{ pathname: "/signUp" }}>Yeni Üye Ol</Link>
                  </div>
                </form>

                <div className="text-center mt-md-2 mt-2">
                  <p>
                    <a
                      href="tel:08503351358"
                      className="text-gray-darkest text-decoration-none"
                    >
                      <i className="fas fa-phone-alt me-3"></i>0850 335 1358
                    </a>
                  </p>
                  <span className="fw-lighter opacity-50">
                    Copyright © 2024 Jarvis. Tüm Hakları Saklıdır.
                  </span>
                  <p className="small mb-0 opacity-50 d-flex justify-content-center flex-nowrap">
                    <button
                      type="button"
                      className="text-decoration-none text-reset border-0 bg-transparent"
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        paddingRight: 2,
                      }}
                      onClick={() => setShowAydinlatmaModal(true)}
                    >
                      Aydınlatma Metni
                    </button>
                    <span style={{ fontSize: "12px" }}>|</span>
                    <button
                      type="button"
                      className="text-decoration-none text-reset border-0 bg-transparent"
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        paddingLeft: 2,
                        paddingRight: 2,
                      }}
                      onClick={() => setShowCerezPolitikasiModal(true)}
                    >
                      Çerez Politikası
                    </button>
                    <span style={{ fontSize: "12px" }}>|</span>
                    <button
                      type="button"
                      className="text-decoration-none text-reset border-0 bg-transparent"
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        paddingLeft: 2,
                      }}
                      onClick={() => setShowKisiselVerilerModal(true)}
                    >
                      Kişisel Veriler Bildirimi
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
          {(() => {
            const hasLoginText =
              loginSettings?.loginPageTitle || loginSettings?.loginPageDescription;

            // Yazı yoksa banner sağ tarafı tamamen kaplasın
            if (!hasLoginText) {
              return (
                <div className="col-lg-6 d-lg-block d-none bg-blue p-0">
                  {loginSettings?.banner && (
                    <img
                      src={loginSettings?.banner}
                      alt=""
                      className="w-100"
                      style={{
                        objectFit: "cover",
                        height: "100vh",
                        display: "block",
                      }}
                    />
                  )}
                </div>
              );
            }

            return (
              <div className="col-lg-6 d-lg-block d-none bg-blue">
                <div className="row min-vh-100 align-items-center justify-content-center">
                  <div className="col-xxl-8 col-lg-10 py-md-8 py-5">
                    <div className="swiper sliderLogin">
                      <div className="swiper-wrapper mb-md-8 mb-5">
                        {loginSettings?.banner && (
                          <div className="bg-none">
                            <div className="text-white">
                              <img
                                src={loginSettings?.banner}
                                alt=""
                                className="login-banner mb-5"
                              />

                              {loginSettings?.loginPageTitle && (
                                <h2 className="text-white mb-3">
                                  {loginSettings?.loginPageTitle}
                                </h2>
                              )}

                              {loginSettings?.loginPageDescription && (
                                <p className="mb-4">
                                  {loginSettings?.loginPageDescription}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="swiper-pagination swiper-pagination-white"></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {showConfirmModal && (
        <div
          className="modal fade show"
          id="confirmModal"
          tabIndex="-1"
          aria-labelledby="confirmModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          data-bs-backdrop="static"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="confirmModalLabel">
                  Hesabınızı doğrulayın
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => setShowConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-center">
                  Kalan Süre:&nbsp;
                  <span style={{ fontWeight: 600 }}>{formatTime(timer)}</span>
                </p>
                <p>
                  Hesabınızı doğrulamak için telefon numaranıza{" "}
                  <strong>SMS</strong> ile gönderilen doğrulama kodunu giriniz.
                  {confirmMobileNo && (
                    <>
                      <br />
                      <strong>{maskPhone(confirmMobileNo)}</strong>
                    </>
                  )}
                </p>
                {timerExpired && (
                  <p className="text-center text-danger">
                    Süreniz doldu. Lütfen "Tekrar Kod Gönder" butonuna basarak
                    yeni kod isteyiniz.
                  </p>
                )}
                <div className="form-floating shadow-lg mb-4 rounded-3">
                  <input
                    type="text"
                    className="form-control border-0 rounded-3 px-4"
                    placeholder="Doğrulama Kodu"
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value)}
                    disabled={timerExpired}
                  />
                  <label htmlFor="loginConfirmCode" className="px-4">
                    <i className="fas fa-user me-3"></i>Doğrulama Kodu
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                {timerExpired ? (
                  <button
                    type="button"
                    className="w-100 btn btn-blue py-2 px-sm-4 px-3 rounded-3"
                    onClick={handleResendCode}
                    disabled={!confirmMobileNo}
                  >
                    Tekrar Kod Gönder <i className="fas fa-redo ms-3"></i>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="w-100 btn btn-blue py-2 px-sm-4 px-3 rounded-3"
                    onClick={handleConfirmCode}
                    disabled={!confirmMobileNo || !confirmCode}
                  >
                    Gönder <i className="fas fa-arrow-right ms-3"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Aydınlatma Metni Modal */}
      <Modal
        show={showAydinlatmaModal}
        onHide={() => setShowAydinlatmaModal(false)}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Aydınlatma Metni</Modal.Title>
          <button
            type="button"
            className="border-0 bg-transparent"
            onClick={() => setShowAydinlatmaModal(false)}
          >
            <i className="fas fa-times"></i>
          </button>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <LegalText type={LEGAL_TEXT_TYPES.AYDINLATMA_METNI} />
          </div>
        </Modal.Body>
      </Modal>

      {/* Kişisel Veriler Hakkında Bildirim Modal */}
      <Modal
        show={showKisiselVerilerModal}
        onHide={() => setShowKisiselVerilerModal(false)}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Kişisel Veriler Hakkında Bildirim</Modal.Title>
          <button
            type="button"
            className="border-0 bg-transparent"
            onClick={() => setShowKisiselVerilerModal(false)}
          >
            <i className="fas fa-times"></i>
          </button>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <LegalText type={LEGAL_TEXT_TYPES.KISISEL_VERILER_BILDIRIMI} />
          </div>
        </Modal.Body>
      </Modal>

      {/* Çerez Politikası Modal */}
      <Modal
        show={showCerezPolitikasiModal}
        onHide={() => setShowCerezPolitikasiModal(false)}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Çerez Politikası</Modal.Title>
          <button
            type="button"
            className="border-0 bg-transparent"
            onClick={() => setShowCerezPolitikasiModal(false)}
          >
            <i className="fas fa-times"></i>
          </button>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <LegalText type={LEGAL_TEXT_TYPES.CEREZ_POLITIKASI} />
          </div>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default Login;
